import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Chip,
  Button,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  IconButton,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Check as CheckIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { products, categories } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';

const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { addItem, isInCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  
  const [quantity, setQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Find the product
  const product = products.find(p => p.id === productId);
  
  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error" gutterBottom>
          {t.common.error}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Produit non trouvé
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/products')}
          sx={{ mt: 2 }}
        >
          Retour aux produits
        </Button>
      </Container>
    );
  }

  // Find category info
  const category = categories.find(c => c.id === product.category);

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString() + ' FCFA';
  };

  // Handle quantity change
  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (value > 0 && value <= product.stock) {
      setQuantity(value);
    }
  };

  // Handle add to cart
  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.nameFr,
      price: product.price,
      image: product.image,
      stock: product.stock,
    });
    setSnackbarMessage(`${product.nameFr} ajouté au panier`);
    setSnackbarOpen(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Handle favorite toggle
  const handleFavoriteToggle = () => {
    toggleFavorite({
      id: product.id,
      name: product.nameFr,
      price: product.price,
      image: product.image,
      category: product.category,
      rating: product.rating,
      reviews: product.reviews,
      isNew: product.isNew,
      isFeatured: product.isFeatured,
      stock: product.stock,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" color="inherit">
          {t.navigation.home}
        </Link>
        <Link component={RouterLink} to="/products" color="inherit">
          {t.navigation.products}
        </Link>
        {category && (
          <Link component={RouterLink} to={`/products?category=${category.id}`} color="inherit">
            {category.nameFr}
          </Link>
        )}
        <Typography color="text.primary">{product.nameFr}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Card sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              height={400}
              image={product.image}
              alt={product.nameFr}
              sx={{ objectFit: 'cover' }}
            />
            <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleFavoriteToggle}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}
              >
                {isFavorite(product.id) ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
              </IconButton>
              <IconButton sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}>
                <ShareIcon />
              </IconButton>
            </Box>
            <Box sx={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 1 }}>
              {product.isNew && (
                <Chip label={t.common.new} color="secondary" />
              )}
              {product.isFeatured && (
                <Chip label={t.common.featured} color="primary" />
              )}
              {product.stock < 50 && (
                <Chip label={t.common.lowStock} color="warning" />
              )}
            </Box>
          </Card>
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Title and Rating */}
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.nameFr}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Rating value={product.rating} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary">
                  {product.rating} ({product.reviews} avis)
                </Typography>
              </Box>
              <Typography variant="h5" color="primary" gutterBottom>
                {formatPrice(product.price)}
              </Typography>
            </Box>

            {/* Description */}
            <Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                {product.descriptionFr}
              </Typography>
            </Box>

            {/* Stock Status */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Stock: {product.stock} unités disponibles
              </Typography>
            </Box>

            {/* Quantity and Add to Cart */}
            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  type="number"
                  label={t.common.quantity}
                  value={quantity}
                  onChange={handleQuantityChange}
                  inputProps={{ min: 1, max: product.stock }}
                  sx={{ width: 120 }}
                />
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CartIcon />}
                  onClick={handleAddToCart}
                  disabled={product.stock === 0 || isInCart(product.id)}
                  sx={{ flex: 1 }}
                >
                  {product.stock === 0 
                    ? t.common.outOfStock 
                    : isInCart(product.id) 
                      ? 'Dans le panier' 
                      : t.common.addToCart
                  }
                </Button>
              </Stack>
            </Box>

            {/* Features */}
            {product.featuresFr && product.featuresFr.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t.products.features}
                </Typography>
                <List dense>
                  {product.featuresFr.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Specifications and Details */}
      <Box sx={{ mt: 6 }}>
        <Grid container spacing={4}>
          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t.products.specifications}
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <Grid item xs={12} key={key}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {key}:
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Reviews */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t.products.reviews} ({product.reviews})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={product.rating} precision={0.1} readOnly />
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {product.rating} sur 5
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Basé sur {product.reviews} avis clients
              </Typography>
              <Button variant="outlined" sx={{ mt: 2 }}>
                Voir tous les avis
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Related Products */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom>
          Produits similaires
        </Typography>
        <Grid container spacing={3}>
          {products
            .filter(p => p.category === product.category && p.id !== product.id)
            .slice(0, 4)
            .map((relatedProduct) => (
              <Grid item xs={12} sm={6} md={3} key={relatedProduct.id}>
                <Card
                  component={RouterLink}
                  to={`/product/${relatedProduct.id}`}
                  sx={{
                    textDecoration: 'none',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardMedia
                    component="img"
                    height={200}
                    image={relatedProduct.image}
                    alt={relatedProduct.nameFr}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {relatedProduct.nameFr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {relatedProduct.descriptionFr}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatPrice(relatedProduct.price)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductDetail; 